import React from "react";
import {
  FaEnvelope,
  FaExclamationTriangle,
  FaPhoneAlt,
} from "react-icons/fa";

import {
  getFullName,
  normalizePhoneForHref,
} from "../utils/judokaProfileUtils";

function EmptyContact({ icon: Icon, label }) {
  return (
    <div className="flex min-h-14 items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-white/60 px-4 py-3 text-slate-500 dark:border-white/10 dark:bg-white/[0.04] dark:text-white/40">
      <Icon className="shrink-0" />

      <span className="text-sm font-bold">
        {label}
      </span>
    </div>
  );
}

function EmergencyContactCard({ judoka }) {
  const phone = String(
    judoka?.tel_adherent || "",
  ).trim();

  const email = String(
    judoka?.mail_adherent || "",
  ).trim();

  const normalizedPhone =
    normalizePhoneForHref(phone);

  const hasPhone =
    Boolean(phone) && Boolean(normalizedPhone);

  const hasEmail = Boolean(email);

  const fullName = getFullName(judoka);

  const emailSubject = encodeURIComponent(
    `Judo Coach Pro - Contact concernant ${fullName}`,
  );

  return (
    <section className="overflow-hidden rounded-[2rem] border border-red-200 bg-white shadow-xl shadow-red-100/60 dark:border-red-400/20 dark:bg-white/[0.05] dark:shadow-black/20">
      <div className="relative overflow-hidden bg-gradient-to-br from-red-600 via-red-600 to-orange-500 p-5 text-white sm:p-6">
        <div className="absolute -right-12 -top-12 h-36 w-36 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-16 left-20 h-32 w-32 rounded-full bg-orange-200/20 blur-2xl" />

        <div className="relative flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/15 shadow-lg backdrop-blur">
            <FaExclamationTriangle />
          </div>

          <div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-white/70">
              Accès rapide
            </p>

            <h2 className="mt-1 font-titre text-2xl font-bold">
              Contact d’urgence
            </h2>

            <p className="mt-2 max-w-xl text-sm font-medium leading-6 text-white/80">
              Utilisez les coordonnées
              associées au profil pour
              contacter rapidement le judoka
              ou son responsable.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-5 sm:p-6">
        <div className="grid gap-3 sm:grid-cols-2">
          {hasPhone ? (
            <a
              href={`tel:${normalizedPhone}`}
              className="group flex min-h-16 items-center gap-3 rounded-2xl bg-red-600 px-4 py-3 text-white shadow-lg shadow-red-500/20 transition hover:-translate-y-0.5 hover:bg-red-700"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15">
                <FaPhoneAlt />
              </span>

              <span className="min-w-0">
                <span className="block text-xs font-bold uppercase tracking-[0.16em] text-white/65">
                  Appeler
                </span>

                <span className="mt-1 block truncate text-sm font-black">
                  {phone}
                </span>
              </span>
            </a>
          ) : (
            <EmptyContact
              icon={FaPhoneAlt}
              label="Téléphone non renseigné"
            />
          )}

          {hasEmail ? (
            <a
              href={`mailto:${email}?subject=${emailSubject}`}
              className="group flex min-h-16 items-center gap-3 rounded-2xl bg-sky-600 px-4 py-3 text-white shadow-lg shadow-sky-500/20 transition hover:-translate-y-0.5 hover:bg-sky-700"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15">
                <FaEnvelope />
              </span>

              <span className="min-w-0">
                <span className="block text-xs font-bold uppercase tracking-[0.16em] text-white/65">
                  Envoyer un e-mail
                </span>

                <span className="mt-1 block truncate text-sm font-black">
                  {email}
                </span>
              </span>
            </a>
          ) : (
            <EmptyContact
              icon={FaEnvelope}
              label="E-mail non renseigné"
            />
          )}
        </div>

        {!hasPhone && !hasEmail && (
          <div className="rounded-2xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm font-semibold leading-6 text-orange-800 dark:border-orange-400/20 dark:bg-orange-500/10 dark:text-orange-200">
            Aucune coordonnée n’est
            actuellement renseignée. Utilisez
            le bouton « Modifier » pour ajouter
            un téléphone ou une adresse
            e-mail.
          </div>
        )}
      </div>
    </section>
  );
}

export default EmergencyContactCard;